import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Landmark, Lock } from "lucide-react";
import { toast } from "sonner";
import { actionCard, dismissCard, getBalance, getBankStatus, getCards, getNotifications, getSubscription, runAgent } from "@/lib/api";
import { useMediaQuery } from "@/hooks/use-media-query";
import { BalanceHeader } from "@/components/BalanceHeader";
import { InsightCard } from "@/components/InsightCard";
import { ChatSheet } from "@/components/ChatSheet";
import { CardDetailSheet } from "@/components/CardDetailSheet";
import { BankSheet } from "@/components/BankSheet";
import { AnalyticsSheet } from "@/components/AnalyticsSheet";
import { TaxSheet } from "@/components/TaxSheet";
import { SubscriptionsSheet } from "@/components/SubscriptionsSheet";
import { NotificationsSheet } from "@/components/NotificationsSheet";
import { InvestmentsSheet } from "@/components/InvestmentsSheet";
import { MonthlyReportSheet } from "@/components/MonthlyReportSheet";
import { MonthSheet } from "@/components/MonthSheet";
import { AccountSheet } from "@/components/AccountSheet";
import { PaywallSheet } from "@/components/PaywallSheet";
import { TrialBanner } from "@/components/TrialBanner";
import { HomeDesktop } from "@/components/HomeDesktop";

export default function Home() {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const [balance, setBalance] = useState(null);
  const [cards, setCards] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [subsOpen, setSubsOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifUnread, setNotifUnread] = useState(0);
  const [investOpen, setInvestOpen] = useState(false);
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [bankStatus, setBankStatus] = useState(null);
  const [detailCard, setDetailCard] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showHint, setShowHint] = useState(() => !localStorage.getItem("liqo_swipe_hint"));

  const load = useCallback(async () => {
    const [b, c] = await Promise.all([getBalance(), getCards()]);
    setBalance(b);
    setCards(c);
    setLastUpdated(Date.now());
  }, []);

  const loadBank = useCallback(async () => {
    try {
      setBankStatus(await getBankStatus());
    } catch (e) {
      // La banca è una funzione opzionale: non blocca la Home, ma logghiamo per il debug.
      console.warn("[Liqo] getBankStatus fallito:", e);
    }
  }, []);

  const loadNotif = useCallback(async () => {
    try {
      const n = await getNotifications();
      setNotifUnread(n.unread_count || 0);
    } catch (e) {
      console.warn("[Liqo] getNotifications fallito:", e);
    }
  }, []);

  const loadSubscription = useCallback(async () => {
    try {
      setSubscription(await getSubscription());
    } catch (e) {
      console.warn("[Liqo] getSubscription fallito:", e);
    }
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Errore nel caricamento dei dati"));
    loadBank();
    loadNotif();
    loadSubscription();
  }, [load, loadBank, loadNotif, loadSubscription]);

  // Premium: ottimistico finché lo stato non è caricato (gli utenti partono in prova).
  const isPremium = subscription ? subscription.is_premium : true;
  // Founder (owner di Liqo): badge dedicato + Pro a vita (nessun countdown prova).
  const isFounder = !!subscription?.is_founder;
  // Apre una funzione Pro oppure il paywall se l'utente non è più premium.
  const gated = (openFn) => () => (isPremium ? openFn() : setPaywallOpen(true));

  const markHintSeen = () => {
    if (showHint) {
      localStorage.setItem("liqo_swipe_hint", "1");
      setShowHint(false);
    }
  };

  const handleDismiss = async (id) => {
    markHintSeen();
    setCards((prev) => prev.filter((c) => c.id !== id));
    try {
      await dismissCard(id);
    } catch {
      toast.error("Errore di rete: riprova");
      load();
    }
  };

  const handleResolve = async (id, label) => {
    setCards((prev) => prev.filter((c) => c.id !== id));
    try {
      await actionCard(id, label);
      toast.success(`Segnato: ${label}`);
    } catch {
      toast.error("Errore di rete: riprova");
      load();
    }
  };

  const handleDetail = (card) => {
    markHintSeen();
    if (card.type === "TAX") {
      setTaxOpen(true);
      return;
    }
    if (card.type === "SUBSCRIPTION") {
      setSubsOpen(true);
      return;
    }
    if (card.type === "INVESTMENT") {
      gated(() => setInvestOpen(true))();
      return;
    }
    if (card.type === "INVEST_TIP") {
      gated(() => setInvestOpen(true))();
      return;
    }
    if (card.type === "SIGNAL") {
      gated(() => setInvestOpen(true))();
      return;
    }
    if (card.type === "MONTHLY_REPORT") {
      gated(() => setMonthlyOpen(true))();
      return;
    }
    setDetailCard(card);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await runAgent();
      await load();
      await loadNotif();
      if (res?.classifying) {
        toast.success("Analisi avviata — gli insight si aggiornano tra pochi istanti");
        // ricariche ritardate: le card vengono classificate in background
        setTimeout(() => { load(); loadNotif(); }, 12000);
        setTimeout(() => { load(); loadNotif(); }, 30000);
      } else {
        toast("Tutto sotto controllo — nessuna novità");
      }
    } catch {
      toast.error("L'agente non ha risposto: riprova");
    } finally {
      setRefreshing(false);
    }
  };

  const onBankChanged = () => {
    loadBank();
    load().catch(() => {});
    loadNotif();
  };

  return (
    <div data-testid="home-screen" className="lg:h-screen">
      {isDesktop ? (
        <HomeDesktop
          balance={balance}
          cards={cards}
          refreshing={refreshing}
          onRefresh={handleRefresh}
          onDismiss={handleDismiss}
          onResolve={handleResolve}
          onDetail={handleDetail}
          bankStatus={bankStatus}
          notifUnread={notifUnread}
          subscription={subscription}
          isPremium={isPremium}
          isFounder={isFounder}
          onUpgrade={() => setPaywallOpen(true)}
          onBank={() => setBankOpen(true)}
          onAnalytics={() => setAnalyticsOpen(true)}
          onTax={() => setTaxOpen(true)}
          onSubscriptions={() => setSubsOpen(true)}
          onNotifications={() => setNotifOpen(true)}
          onInvestments={gated(() => setInvestOpen(true))}
          onMonthly={gated(() => setMonthlyOpen(true))}
          onMonth={gated(() => setMonthOpen(true))}
          onAccount={() => setAccountOpen(true)}
          onTransactions={() => navigate("/transactions")}
          onAddCash={() => navigate("/transactions?add=cash")}
        />
      ) : (
        <div className="min-h-dvh pb-32">
          <BalanceHeader
            balance={balance}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            lastUpdated={lastUpdated}
            onBank={() => setBankOpen(true)}
            bankConnected={bankStatus?.connected}
            onAnalytics={() => setAnalyticsOpen(true)}
            onTax={() => setTaxOpen(true)}
            onSubscriptions={() => setSubsOpen(true)}
            onNotifications={() => setNotifOpen(true)}
            notifUnread={notifUnread}
            onInvestments={gated(() => setInvestOpen(true))}
            onMonthly={gated(() => setMonthlyOpen(true))}
            onMonth={gated(() => setMonthOpen(true))}
            onAccount={() => setAccountOpen(true)}
            isFounder={isFounder}
          />

          <div className="px-4 pt-4">
            <TrialBanner subscription={subscription} onUpgrade={() => setPaywallOpen(true)} />

            {bankStatus && bankStatus.configured && (
              <button
                onClick={() => setBankOpen(true)}
                data-testid="home-connect-bank-cta"
                className="mb-3 flex w-full items-center gap-3 rounded-[12px] border border-[#00FF87]/25 bg-[#0D0D14] p-3.5 text-left shadow-[0_0_0_1px_rgba(0,255,135,0.08)] hover:bg-[#00FF87]/[0.04] active:scale-[0.99]"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-[#00FF87]/30 text-[#00FF87]">
                  <Landmark size={16} />
                </span>
                <span className="flex-1">
                  <span className="block text-[13px] font-light text-white/85">
                    {bankStatus.connected ? "Aggiungi o gestisci le tue banche" : "Collega la tua banca reale"}
                  </span>
                  <span className="caps-tag mt-0.5 block text-white/40">
                    {bankStatus.connected
                      ? `${bankStatus.connections?.length || 0} ${(bankStatus.connections?.length || 0) === 1 ? "banca collegata" : "banche collegate"} · sync ogni 6h`
                      : "Open Banking · sync ogni 6h"}
                  </span>
                </span>
                <span className="mono text-xs text-[#00FF87]/70">❯</span>
              </button>
            )}

            {cards === null ? (
              <div className="space-y-3" data-testid="feed-loading">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4">
                    <div className="h-2.5 w-28 animate-pulse bg-white/[0.06]" />
                    <div className="mt-4 h-8 w-32 animate-pulse bg-white/[0.08]" />
                    <div className="mt-3 h-3 w-3/4 animate-pulse bg-white/[0.05]" />
                    <div className="mt-5 flex gap-2">
                      <div className="h-8 flex-1 animate-pulse rounded-[6px] bg-white/[0.05]" />
                      <div className="h-8 flex-1 animate-pulse rounded-[6px] bg-white/[0.04]" />
                    </div>
                  </div>
                ))}
              </div>
            ) : cards.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15 }}
                className="flex flex-col items-center pt-24 text-center"
              >
                <span className="mono text-xl text-[#00FF87]">❯<span className="liqo-blink">_</span></span>
                <p className="mono mt-5 text-base font-light text-white/90" data-testid="empty-state-text">
                  Niente di urgente oggi.
                </p>
                <p className="mt-2 max-w-[260px] text-[13px] font-light text-white/40">
                  L'agente ti avviserà quando ci sarà qualcosa di rilevante.
                </p>
              </motion.div>
            ) : (
              <>
                {showHint && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="caps-tag mb-3 text-center text-white/25"
                    data-testid="swipe-hint"
                  >
                    Swipe → capito · ← dettagli
                  </motion.p>
                )}
                <div className="space-y-3">
                  <AnimatePresence mode="popLayout">
                    {cards.map((card, i) => (
                      <InsightCard
                        key={card.id}
                        card={card}
                        index={i}
                        onDismiss={handleDismiss}
                        onDetail={handleDetail}
                        onResolve={handleResolve}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>

          {/* FAB chat — terminale (solo mobile) */}
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            onClick={gated(() => setChatOpen(true))}
            data-testid="open-chat-fab"
            aria-label="Apri chat CFO"
            className="mono fixed bottom-16 z-40 flex h-14 w-14 items-center justify-center rounded-[10px] border border-[#00FF87]/40 bg-[#0D0D14] text-xs font-medium tracking-[0.15em] text-[#00FF87] shadow-[0_0_24px_rgba(0,255,135,0.15)] hover:bg-[#00FF87]/[0.06] active:scale-[0.96] right-[max(1.5rem,calc(50vw-215px+1.5rem))]"
          >
            CFO
            {!isPremium && (
              <span
                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-[#00FF87]/50 bg-[#030305] text-[#00FF87]"
                data-testid="chat-fab-lock"
              >
                <Lock size={10} />
              </span>
            )}
          </motion.button>
        </div>
      )}

      {/* ============ SHEETS CONDIVISI (renderizzati una sola volta) ============ */}
      <ChatSheet open={chatOpen} onClose={() => setChatOpen(false)} />
      <BankSheet open={bankOpen} onClose={() => setBankOpen(false)} onChanged={onBankChanged} />
      <AnalyticsSheet open={analyticsOpen} onClose={() => setAnalyticsOpen(false)} />
      <TaxSheet
        open={taxOpen}
        onClose={() => setTaxOpen(false)}
        onChanged={() => load().catch(() => {})}
      />
      <SubscriptionsSheet open={subsOpen} onClose={() => setSubsOpen(false)} />
      <NotificationsSheet open={notifOpen} onClose={() => setNotifOpen(false)} onRead={loadNotif} />
      <AccountSheet
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        subscription={subscription}
        onUpgrade={() => setPaywallOpen(true)}
        onSubscriptionChange={(s) => (s ? setSubscription(s) : loadSubscription())}
      />
      <InvestmentsSheet
        open={investOpen}
        onClose={() => setInvestOpen(false)}
        onChanged={() => {
          load().catch(() => {});
          loadNotif();
        }}
      />
      <CardDetailSheet
        card={detailCard}
        onClose={() => setDetailCard(null)}
        onDismissed={(id) => {
          setDetailCard(null);
          setCards((prev) => prev.filter((c) => c.id !== id));
        }}
        onExcluded={() => {
          // L'esclusione del giroconto ricalcola insight e saldo in background.
          loadNotif();
          setTimeout(() => { load(); loadNotif(); }, 4000);
          setTimeout(() => { load(); loadNotif(); }, 12000);
        }}
      />
      <MonthlyReportSheet
        open={monthlyOpen}
        onClose={() => setMonthlyOpen(false)}
        onOpenInvest={() => setInvestOpen(true)}
      />
      <MonthSheet open={monthOpen} onClose={() => setMonthOpen(false)} />
      <PaywallSheet
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        subscription={subscription}
        onSubscribed={(s) => {
          setSubscription(s);
          load().catch(() => {});
        }}
      />
    </div>
  );
}
